import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type PruningQuantityTreePropertyDescriptor,
  type TreeDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { RadioGroup, RadioGroupItem } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

interface PruningQuantityRadioItemProps {
  checked?: boolean;
  onClick?: () => void;
  subTitle?: string;
  title: string;
  value: string;
}

function PruningQuantityRadioItem({
  checked,
  onClick,
  subTitle,
  title,
  value,
}: PruningQuantityRadioItemProps) {
  return (
    <div
      aria-checked={checked}
      data-state={checked ? "checked" : "unchecked"}
      className="data-[state=checked]:bg-blueBgDefault flex items-center gap-3 rounded px-3 py-2"
      onClick={onClick}
      role="radio"
    >
      <RadioGroupItem value={value} />

      <div>
        <p className="text-bodymd text-textPrimary">{title}</p>
        <p className="text-bodyxs text-subdued">{subTitle}</p>
      </div>
    </div>
  );
}

export interface PruningQuantityFieldProps {
  onChange: ((treeDescriptor: TreeDescriptor) => void) | undefined;
  treeDescriptor: TreeDescriptor;
}

export function PruningQuantityField<
  T extends PruningQuantityTreePropertyDescriptor,
>({ onChange, treeDescriptor }: PruningQuantityFieldProps) {
  const { isValid, messages, shouldShowValidation } = useTreeDialogValidator<T>(
    {
      checker: (property) => !!property?.value,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.pruning_quantity,
    },
  );

  const pruningQuantity = treeDescriptor.properties.get(
    TreePropertyType.pruning_quantity,
  ) as T | undefined;

  const handleChange = (value: string) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.pruning_quantity,
      (draft) => {
        draft.value = value as T["value"];
      },
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel isValid={isValid}>
        {getText("How much pruning are you proposing to do?")}
      </FieldLabel>

      <RadioGroup
        aria-invalid={!isValid && shouldShowValidation}
        className="flex flex-col gap-1.5"
        onValueChange={handleChange}
        value={pruningQuantity?.value}
      >
        <PruningQuantityRadioItem
          checked={pruningQuantity?.value === "minor"}
          onClick={() => handleChange("minor")}
          subTitle={getText("Less than 10% of the foliage and branches")}
          title={getText("Minor trimming only")}
          value="minor"
        />

        <PruningQuantityRadioItem
          checked={pruningQuantity?.value === "moderate"}
          onClick={() => handleChange("moderate")}
          subTitle={getText("Up to 30% of the foliage and branches")}
          title={getText("Moderate reduction")}
          value="moderate"
        />

        <PruningQuantityRadioItem
          checked={pruningQuantity?.value === "significant"}
          onClick={() => handleChange("significant")}
          subTitle={getText("30%+ of the foliage or lopping large branches")}
          title={getText("Lopping or significant reduction")}
          value="significant"
        />
      </RadioGroup>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
